// src/config/claimStatuses.js
//
// Estados de Organized Adjuster (Settled, Under Review, Awaiting Coverage…) como
// LISTA EDITABLE, separada de los pasos del flujo (config/claimFlow.js). Cada
// reclamo guarda el nombre del estado en `oaStatus`; el catálogo (nombre + color)
// vive en Firestore `config/claimStatuses` y lo edita el admin. Mientras el
// documento no exista se usan estos valores por defecto (no se escriben solos).

// Colores para elegir. El texto de la etiqueta se calcula solo (blanco o negro
// según el fondo, contrastText), así cualquier color elegido, incluso uno
// personalizado, mantiene al menos 4.5:1 de contraste.
export const STATUS_COLORS = [
  '#2563eb', '#0891b2', '#0d9488', '#16a34a', '#65a30d', '#f59e0b',
  '#ea580c', '#dc2626', '#db2777', '#9333ea', '#4f46e5', '#64748b'
];

export const DEFAULT_STATUSES = [
  { id: 'active', name: 'Active', color: '#65a30d' },
  { id: 'prospect', name: 'Prospect', color: '#0891b2' },
  { id: 'under-review', name: 'Under Review', color: '#f59e0b' },
  { id: 'awaiting-coverage', name: 'Awaiting Coverage', color: '#ea580c' },
  { id: 'negotiation', name: 'Negotiation', color: '#2563eb' },
  { id: 'appraisal', name: 'Appraisal', color: '#4f46e5' },
  { id: 'mediation', name: 'Mediation', color: '#0d9488' },
  { id: 'scheduling-mediation', name: 'Scheduling Mediation', color: '#0d9488' },
  { id: 'attorney', name: 'Attorney', color: '#9333ea' },
  { id: 'pre-litigation', name: 'Pre-Litigation', color: '#db2777' },
  { id: 'settled', name: 'Settled', color: '#16a34a' },
  { id: 'archive', name: 'Archive', color: '#64748b' },
  { id: 'closed', name: 'Closed', color: '#64748b' }
];

const HEX = /^#[0-9a-f]{6}$/i;
export const isHexColor = value => HEX.test(String(value || ''));

const channel = value => {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};
const luminance = hex => {
  const n = parseInt(hex.slice(1), 16);
  return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
};
export const contrastRatio = (a, b) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};
// Texto blanco o negro, el que más contraste dé sobre el fondo elegido.
export const contrastText = hex => (isHexColor(hex) && contrastRatio(hex, '#ffffff') >= contrastRatio(hex, '#111111') ? '#ffffff' : '#111111');

export const normalizeName = name => String(name || '').trim().replace(/\s+/g, ' ');
const key = name => normalizeName(name).toLowerCase();

// Busca por nombre sin distinguir mayúsculas. null = sin estado o sin catálogo.
export const findStatus = (catalog, name) => {
  const wanted = key(name);
  return wanted ? (catalog || []).find(item => key(item.name) === wanted) || null : null;
};

export const slugify = name => key(name).normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'estado';

// Limpia una lista antes de guardarla: nombres únicos y colores válidos.
export const sanitizeCatalog = items => {
  const seen = new Set();
  const out = [];
  (items || []).forEach(item => {
    const name = normalizeName(item?.name);
    if (!name || seen.has(key(name))) return;
    seen.add(key(name));
    out.push({ id: item.id || slugify(name), name, color: isHexColor(item.color) ? item.color.toLowerCase() : STATUS_COLORS[0] });
  });
  return out;
};

// Lee del historial el estado original con el que se importó un reclamo:
// "Importado de Organized (Settled)" o "Organized: Settled".
export const statusFromHistoryReason = reason => {
  const text = String(reason || '').trim();
  const match = /^Importado de Organized \((.+)\)$/.exec(text) || /^Organized: (.+)$/.exec(text);
  const value = match ? normalizeName(match[1]) : '';
  return value && value.toLowerCase() !== 'sin estado' ? value : '';
};
